import { useState } from "react";
import { Link } from "react-router-dom";
import { Wrench } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/common";
import { DueText, slotLabel } from "@/modules/followups/shared";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";
import { t } from "@/i18n";

const brand = { background: "var(--brand)", color: "var(--brand-contrast)" };

function Proposal({ p, slug, tz, onDone }) {
  const [start, setStart] = useState(p.slots[0]);
  const [mode, setMode] = useState(null);
  const [day, setDay] = useState("");
  const [others, setOthers] = useState(null);
  const [alts, setAlts] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const call = async (fn) => {
    setBusy(true);
    try { await fn(); onDone(); } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.alternatives) { setAlts(d.alternatives); setStart(d.alternatives[0]); }
      toast.error(errMsg(e));
    } finally { setBusy(false); }
  };
  const accept = () => call(async () => {
    const { data } = await api.post(`/portal/${slug}/proposals/${p.id}/accept`, { start });
    toast.success(data.appointment.status === "confirmed" ? t("fu.portal.confirmed") : t("fu.portal.pending"));
  });
  const respond = (kind) => call(async () => { await api.post(`/portal/${slug}/proposals/${p.id}/${kind}`, { comment }); toast.success(t("fu.portal.thanks")); });
  const loadDay = async (d) => { setDay(d); setOthers(null); if (d) setOthers((await api.get(`/portal/${slug}/proposals/${p.id}/slots`, { params: { day: d } })).data); };
  const choices = alts || (mode === "other" ? others || [] : p.slots);
  return (
    <div className="mt-4 rounded-lg border border-slate-200 p-3" data-testid={`portal-proposal-${p.id}`}>
      <p className="text-sm font-semibold">{t("fu.portal.proposed", { service: p.service_name })}</p>
      {p.note && <p className="text-xs text-slate-600">{p.note}</p>}
      <p className="mt-1 text-xs text-slate-500">{t("fu.portal.notBooked")}</p>
      {alts && <p className="mt-2 text-xs font-semibold text-amber-700" data-testid="portal-proposal-alternatives">{t("fu.portal.gone")}</p>}
      {mode === "other" && !alts && <Input type="date" className="mt-2 max-w-xs" value={day} onChange={(e) => loadDay(e.target.value)} data-testid="portal-proposal-day-input" />}
      <div className="mt-2 flex flex-wrap gap-2">
        {choices.map((s) => <button key={s} type="button" onClick={() => setStart(s)} data-testid="portal-proposal-slot"
          className={`rounded-full border px-3 py-1 text-xs font-semibold ${start === s ? "border-transparent" : "border-slate-200 bg-white"}`} style={start === s ? brand : undefined}>{slotLabel(s, tz)}</button>)}
        {mode === "other" && others && others.length === 0 && <p className="text-xs text-slate-500">{t("sched.noSlots")}</p>}
      </div>
      {(mode === "change" || mode === "decline") && <Textarea className="mt-2" placeholder={t("fu.portal.commentPh")} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="portal-proposal-comment" />}
      <div className="mt-3 flex flex-wrap gap-2">
        {mode === "change" ? <Button size="sm" style={brand} disabled={busy} onClick={() => respond("request-change")} data-testid="portal-proposal-send-change">{t("fu.portal.sendChange")}</Button>
          : mode === "decline" ? <Button size="sm" variant="destructive" disabled={busy} onClick={() => respond("decline")} data-testid="portal-proposal-confirm-decline">{t("fu.portal.confirmDecline")}</Button>
            : <Button size="sm" style={brand} disabled={busy || !start || !choices.includes(start)} onClick={accept} data-testid="portal-proposal-accept">{t("fu.portal.accept")}</Button>}
        {!alts && mode !== "other" && <Button size="sm" variant="outline" onClick={() => setMode("other")} data-testid="portal-proposal-other">{t("fu.portal.other")}</Button>}
        {mode !== "change" && <Button size="sm" variant="outline" onClick={() => setMode("change")} data-testid="portal-proposal-change">{t("fu.portal.change")}</Button>}
        {mode !== "decline" && <Button size="sm" variant="ghost" onClick={() => setMode("decline")} data-testid="portal-proposal-decline">{t("fu.portal.decline")}</Button>}
      </div>
    </div>
  );
}

export function NextServiceCard({ slug, tz, canBook }) {
  const { data, reload } = useFetch(`/portal/${slug}/followups`);
  if (!data || data.items.length === 0) return null;
  return (
    <Card testId="portal-next-service">
      <Wrench className="h-5 w-5" style={{ color: "var(--brand)" }} />
      <p className="mt-3 font-display text-lg font-bold">{t("fu.portal.title")}</p>
      {data.items.map((i) => (
        <div key={i.id} className="mt-3 border-t border-slate-100 pt-3" data-testid={`portal-maintenance-${i.id}`}>
          <p className="text-sm font-semibold">{i.recommendation}</p>
          <p className="text-xs text-slate-500">{i.vehicle?.plate} · {i.vehicle?.make} {i.vehicle?.model}</p>
          <DueText due={i.due} className="mt-1" />
          {i.proposals.filter((p) => p.status === "pending").map((p) => <Proposal key={p.id} p={p} slug={slug} tz={tz} onDone={reload} />)}
          {i.proposals.some((p) => ["declined", "change_requested"].includes(p.status)) && <p className="mt-2 text-xs text-slate-500">{t("fu.portal.answered")}</p>}
          {canBook && !i.proposals.some((p) => p.status === "pending") && <Button asChild size="sm" variant="outline" className="mt-2" data-testid="portal-maintenance-book"><Link to={`/g/${slug}/book`}>{t("book.cta")}</Link></Button>}
        </div>
      ))}
    </Card>
  );
}
